<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  MODES,
  MODE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  createLane,
  deleteLane,
  laneFormError,
  laneInput,
  listLanes,
  siteLabel,
  updateLane,
  type LaneForm,
  type LaneMode,
  type LaneRow,
  type LaneStatus,
} from '../api/lanes'
import { listSiteOptions, type SiteRow } from '../api/sites'

const rows = ref<LaneRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
/** 删除失败：路径被还能运行的仿真任务列进 candidate_lane_ids 时后端 409 */
const refError = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', mode: '', page: 1, size: 20 })

/** 网点参照：列表的「起点 → 终点」与表单两端下拉共用同一份 */
const sites = ref<SiteRow[]>([])
/** 参照拉取失败只影响文案与下拉，不挡列表：单独提示，不与列表错误混在一起 */
const refNotice = ref('')

const formOpen = ref(false)
const editing = ref<LaneRow | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<LaneForm>({
  code: '',
  name: '',
  from_site_id: undefined,
  to_site_id: undefined,
  distance_km: undefined,
  duration_min: undefined,
  cost_per_km: undefined,
  mode: 'truck',
  status: 'active',
})

const formErrorText = computed(() => laneFormError(form))

/**
 * 下拉里补一条「参照没加载到」的已选项：
 * 参照只取前 200 条网点，编辑一条两端都很老的路径时会选不中，
 * 少了这一条 el-select 会显示成空白，看起来像数据丢了。
 */
function withPicked(list: SiteRow[], picked: number | undefined): SiteRow[] {
  if (!picked || list.some((s) => s.id === picked)) return list
  return [...list, { id: picked, code: `#${picked}`, name: '（参照未加载到）' } as SiteRow]
}

const fromChoices = computed(() => withPicked(sites.value, form.from_site_id))
const toChoices = computed(() => withPicked(sites.value, form.to_site_id))

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listLanes({
      page: query.page,
      size: query.size,
      keyword: query.keyword,
      status: query.status,
      mode: query.mode,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadReference(): Promise<void> {
  refNotice.value = ''
  try {
    sites.value = await listSiteOptions()
  } catch {
    // 没有参照只是「起点 → 终点」显示成 #id、编辑时两端要手输 id，列表本身照常可用
    refNotice.value = '网点参照加载失败：路径两端的名称暂时显示为编号，编辑时请先到「网点」页确认。'
    sites.value = []
  }
}

onMounted(() => {
  void load()
  void loadReference()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    code: '',
    name: '',
    from_site_id: undefined,
    to_site_id: undefined,
    distance_km: undefined,
    duration_min: undefined,
    cost_per_km: undefined,
    mode: 'truck' as LaneForm['mode'],
    status: 'active' as LaneForm['status'],
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

function openEdit(row: LaneRow): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    from_site_id: row.from_site_id,
    to_site_id: row.to_site_id,
    distance_km: row.distance_km,
    duration_min: row.duration_min,
    cost_per_km: row.cost_per_km,
    mode: row.mode,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input = laneInput({ ...form })
  try {
    if (editing.value) await updateLane(editing.value.id, input)
    else await createLane(input)
    formOpen.value = false
    await load(editing.value ? `路径 ${form.code.trim()} 已更新` : `路径 ${form.code.trim()} 已创建`)
  } catch (e) {
    // 编码重复（409）、两端网点不存在（404，含跨租户）都原样把 trace_id 露出来
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: LaneRow): Promise<void> {
  notice.value = ''
  error.value = null
  refError.value = null
  try {
    await deleteLane(row.id)
    await load(`已删除路径「${row.code}」`)
  } catch (e) {
    refError.value = e
  }
}

/** 时长：分钟 → 「1 天 2 小时 30 分」，读表时一眼能看出量级 */
function durationText(minutes: number): string {
  if (!minutes) return '—'
  const day = Math.floor(minutes / 1440)
  const hour = Math.floor((minutes % 1440) / 60)
  const min = minutes % 60
  if (day > 0) return `${day} 天 ${hour} 小时`
  if (hour > 0) return `${hour} 小时 ${min} 分`
  return `${min} 分钟`
}
</script>

<template>
  <div class="yc-lane">
    <header class="yc-lane__bar">
      <el-input
        v-model="query.keyword"
        class="yc-lane__keyword"
        placeholder="编码 / 名称"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.mode" class="yc-lane__ref" placeholder="全部方式" clearable @change="search">
        <el-option v-for="m in MODES" :key="m" :label="MODE_LABELS[m]" :value="m" />
      </el-select>
      <el-select v-model="query.status" class="yc-lane__ref" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'nos:lane:create'" type="primary" @click="openCreate">新建路径</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-lane__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-lane__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>
    <p v-else-if="refNotice" class="yc-lane__hint">{{ refNotice }}</p>

    <el-card v-loading="loading" class="yc-lane__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="编码" min-width="110" />
        <el-table-column label="名称" min-width="130" show-overflow-tooltip>
          <template #default="{ row }">{{ row.name || '—' }}</template>
        </el-table-column>
        <el-table-column label="起点 → 终点" min-width="240" show-overflow-tooltip>
          <template #default="{ row }">
            {{ siteLabel(sites, row.from_site_id) }}
            <span class="yc-lane__arrow">→</span>
            {{ siteLabel(sites, row.to_site_id) }}
          </template>
        </el-table-column>
        <el-table-column label="里程" width="110" align="right">
          <template #default="{ row }">{{ row.distance_km }} km</template>
        </el-table-column>
        <el-table-column label="时长" width="130" align="right">
          <template #default="{ row }">{{ durationText(row.duration_min) }}</template>
        </el-table-column>
        <el-table-column label="每公里成本" width="120" align="right">
          <template #default="{ row }">¥{{ row.cost_per_km }}/km</template>
        </el-table-column>
        <el-table-column label="方式" width="100">
          <template #default="{ row }">
            <el-tag size="small" type="info">{{ MODE_LABELS[row.mode as LaneMode] ?? row.mode }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as LaneStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as LaneStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'nos:lane:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm
              title="确认删除该路径？被仿真任务列进候选路径时不能删除。"
              width="300"
              @confirm="remove(row)"
            >
              <template #reference>
                <el-button v-perm="'nos:lane:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有路径"
        description="路径是仿真算法的边：先在「网点」页建好两端网点，再把直达运输通道连起来。"
      >
        <el-button v-perm="'nos:lane:create'" type="primary" @click="openCreate">新建路径</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-lane__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑路径 · ${editing.code}` : '新建路径'"
      width="560px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-lane__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="编码" :error="form.code.trim() ? '' : '请输入路径编码'">
          <el-input v-model="form.code" maxlength="32" placeholder="如 LN01" />
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="64" placeholder="如 上海外高桥 → 苏州中转，可留空" />
        </el-form-item>

        <el-form-item label="起运网点" :error="form.from_site_id ? '' : '请选择起运网点'">
          <el-select v-model="form.from_site_id" class="yc-lane__full" filterable placeholder="起点">
            <el-option
              v-for="s in fromChoices"
              :key="s.id"
              :label="`${s.code} ${s.name}`.trim()"
              :value="s.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="目的网点" :error="form.to_site_id ? '' : '请选择目的网点'">
          <el-select v-model="form.to_site_id" class="yc-lane__full" filterable placeholder="终点">
            <el-option
              v-for="s in toChoices"
              :key="s.id"
              :label="`${s.code} ${s.name}`.trim()"
              :value="s.id"
            />
          </el-select>
        </el-form-item>

        <div class="yc-lane__grid">
          <el-form-item label="里程（km，必须大于 0）">
            <!-- 上限 / 小数位照抄迁移里的列宽 DECIMAL(10,2)：前端不设比库更紧的界，
                 否则用户能填出一个后端存得下、界面却不许填的值 -->
            <el-input-number
              v-model="form.distance_km"
              class="yc-lane__full"
              :min="0"
              :max="99999999.99"
              :precision="2"
              :step="10"
              controls-position="right"
            />
          </el-form-item>
          <el-form-item label="时长（分钟）">
            <!-- 列宽 INT -->
            <el-input-number
              v-model="form.duration_min"
              class="yc-lane__full"
              :min="0"
              :max="2147483647"
              :precision="0"
              :step="30"
              controls-position="right"
            />
          </el-form-item>
          <el-form-item label="每公里成本（元）">
            <!-- 列宽 DECIMAL(12,4) -->
            <el-input-number
              v-model="form.cost_per_km"
              class="yc-lane__full"
              :min="0"
              :max="99999999.9999"
              :precision="4"
              :step="0.1"
              controls-position="right"
            />
          </el-form-item>
          <el-form-item label="运输方式">
            <el-select v-model="form.mode" class="yc-lane__full">
              <el-option v-for="m in MODES" :key="m" :label="MODE_LABELS[m]" :value="m" />
            </el-select>
          </el-form-item>
        </div>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
          <p class="yc-lane__hint">只有启用中的路径默认算进仿真的「现网」；params 点名了停用路径也照算。</p>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-lane__hint yc-lane__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-lane {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__ref {
    width: 150px;
  }

  &__full {
    width: 100%;
  }

  &__arrow {
    margin: 0 var(--yc-space-1);
    color: var(--yc-text-secondary);
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 var(--yc-space-3);
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__hint {
    margin: 0 0 var(--yc-space-3);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin: var(--yc-space-1) 0 0;
    }
  }
}
</style>
